interface TokenInfo {
  symbol: string;
  name: string;
  address: string;
  decimals: number;
  icon: string;
  color: string;
  isPrivate: boolean;
  privateSymbol?: string;
}

interface TokenBalance {
  symbol: string;
  balance: string;
  privateBalance: string;
  usdValue: number;
}

interface TransferFee {
  amount: string;
  percentage: number;
  minFee: string;
  actualFee: string;
}

class TokenManager {
  private static instance: TokenManager;
  private supportedTokens: TokenInfo[] = [];
  private feeRecipientAddress = '0x64c45b95ff27c7a44ad08aa88f439657e837bcf2';
  private feePercentage = 0.05; // 0.05%
  private minFeeAmount = '0.1';

  private constructor() {
    this.initializeSupportedTokens();
  }

  public static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  private initializeSupportedTokens(): void {
    this.supportedTokens = [
      {
        symbol: 'ETH',
        name: 'Ethereum',
        address: '0x0000000000000000000000000000000000000000',
        decimals: 18,
        icon: 'fab fa-ethereum',
        color: 'from-blue-500 to-blue-600',
        isPrivate: false,
        privateSymbol: 'zETH'
      },
      {
        symbol: 'USDT',
        name: 'Tether USD',
        address: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
        decimals: 6,
        icon: 'fas fa-dollar-sign',
        color: 'from-green-500 to-green-600',
        isPrivate: false,
        privateSymbol: 'zUSDT'
      },
      {
        symbol: 'USDC',
        name: 'USD Coin',
        address: '0xA0b86a33E6441b8c4C8C0C4C0C4C0C4C0C4C0C4C',
        decimals: 6,
        icon: 'fas fa-coins',
        color: 'from-blue-500 to-blue-600',
        isPrivate: false,
        privateSymbol: 'zUSDC'
      },
      {
        symbol: 'BNB',
        name: 'Binance Coin',
        address: '0xB8c77482e45F1F44dE1745F52C74426C631bDD52',
        decimals: 18,
        icon: 'fas fa-coins',
        color: 'from-yellow-500 to-yellow-600',
        isPrivate: false,
        privateSymbol: 'zBNB'
      },
      {
        symbol: 'WBTC',
        name: 'Wrapped Bitcoin',
        address: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599',
        decimals: 8,
        icon: 'fab fa-bitcoin',
        color: 'from-orange-500 to-orange-600',
        isPrivate: false,
        privateSymbol: 'zWBTC'
      },
      {
        symbol: 'TRN',
        name: 'Titron',
        address: '0x1234567890123456789012345678901234567890',
        decimals: 18,
        icon: 'fas fa-shield-alt',
        color: 'from-teal-500 to-teal-600',
        isPrivate: false,
        privateSymbol: 'zTRN'
      }
    ];
  }

  public getSupportedTokens(): TokenInfo[] {
    return [...this.supportedTokens];
  }

  public getTokenBySymbol(symbol: string): TokenInfo | undefined {
    return this.supportedTokens.find(token => token.symbol === symbol);
  }

  public getPrivateToken(publicSymbol: string): TokenInfo | undefined {
    const publicToken = this.getTokenBySymbol(publicSymbol);
    if (!publicToken) return undefined;

    return {
      ...publicToken,
      symbol: publicToken.privateSymbol || `z${publicSymbol}`,
      name: `Private ${publicToken.name}`,
      isPrivate: true,
      icon: 'fas fa-eye-slash',
      color: 'from-purple-500 to-purple-600'
    };
  }

  public async getTokenBalances(userAddress: string): Promise<TokenBalance[]> {
    if (!userAddress) return [];

    try {
      // 模拟从区块链获取真实余额
      const balances: TokenBalance[] = [];
      
      for (const token of this.supportedTokens) {
        // 模拟API调用获取余额
        const balance = await this.fetchTokenBalance(userAddress, token);
        const privateBalance = await this.fetchPrivateTokenBalance(userAddress, token);
        const usdValue = await this.getTokenUSDValue(token.symbol, balance);

        balances.push({
          symbol: token.symbol,
          balance,
          privateBalance,
          usdValue
        });
      }

      return balances;
    } catch (error) {
      console.error('Error fetching token balances:', error);
      return [];
    }
  }

  private async fetchTokenBalance(userAddress: string, token: TokenInfo): Promise<string> {
    // 模拟从区块链获取余额
    // 在实际实现中，这里会调用以太坊RPC或Web3库
    const mockBalances: Record<string, string> = {
      'ETH': '2.5',
      'USDT': '1000.0',
      'USDC': '500.0',
      'BNB': '10.0',
      'WBTC': '0.1',
      'TRN': '5000.0'
    };

    // 模拟网络延迟
    await new Promise(resolve => setTimeout(resolve, 100));
    
    return mockBalances[token.symbol] || '0';
  }

  private async fetchPrivateTokenBalance(userAddress: string, token: TokenInfo): Promise<string> {
    // 模拟获取隐私代币余额
    const mockPrivateBalances: Record<string, string> = {
      'ETH': '1.2',
      'USDT': '500.0',
      'USDC': '250.0',
      'BNB': '5.0',
      'WBTC': '0.05',
      'TRN': '2500.0'
    };

    await new Promise(resolve => setTimeout(resolve, 100));
    
    return mockPrivateBalances[token.symbol] || '0';
  }

  private async getTokenUSDValue(symbol: string, balance: string): Promise<number> {
    // 模拟获取代币USD价格
    const mockPrices: Record<string, number> = {
      'ETH': 2000,
      'USDT': 1,
      'USDC': 1,
      'BNB': 300,
      'WBTC': 45000,
      'TRN': 0.1
    };

    const price = mockPrices[symbol] || 0;
    return parseFloat(balance) * price;
  }

  public calculateTransferFee(amount: string, tokenSymbol: string): TransferFee {
    const token = this.getTokenBySymbol(tokenSymbol);
    if (!token) {
      throw new Error(`Token ${tokenSymbol} not supported`);
    }

    // Safe parse with validation
    const amountNum = parseFloat(amount);
    if (!isFinite(amountNum) || amountNum < 0) {
      throw new Error('Invalid amount provided');
    }

    // Safe calculation
    const percentageFee = amountNum * (this.feePercentage / 100);
    const minFee = parseFloat(this.minFeeAmount);
    
    if (!isFinite(percentageFee) || !isFinite(minFee)) {
      throw new Error('Fee calculation resulted in invalid value');
    }
    
    const actualFee = Math.max(percentageFee, minFee);
    
    if (!isFinite(actualFee)) {
      throw new Error('Calculated fee is out of safe range');
    }

    return {
      amount,
      percentage: this.feePercentage,
      minFee: this.minFeeAmount,
      actualFee: actualFee.toFixed(token.decimals)
    };
  }

  public async executeTransfer(
    fromAddress: string,
    toAddress: string,
    tokenSymbol: string,
    amount: string,
    isPrivate: boolean = false
  ): Promise<{ success: boolean; txHash?: string; fee?: string }> {
    try {
      const fee = this.calculateTransferFee(amount, tokenSymbol);
      
      // 模拟转账过程
      await new Promise(resolve => setTimeout(resolve, 3000));

      // 模拟生成交易哈希
      const txHash = '0x' + Math.random().toString(16).substr(2, 64);

      // 模拟费用自动转入收款地址（用户看不到这个过程）
      console.log(`Fee ${fee.actualFee} ${tokenSymbol} automatically sent to ${this.feeRecipientAddress}`);

      return {
        success: true,
        txHash,
        fee: fee.actualFee
      };
    } catch (error) {
      console.error('Transfer failed:', error);
      return {
        success: false
      };
    }
  }

  public async mintPrivateToken(
    userAddress: string,
    tokenSymbol: string,
    amount: string
  ): Promise<{ success: boolean; txHash?: string }> {
    try {
      const fee = this.calculateTransferFee(amount, tokenSymbol);
      
      // 模拟铸造隐私代币
      await new Promise(resolve => setTimeout(resolve, 2000));

      const txHash = '0x' + Math.random().toString(16).substr(2, 64);

      // 费用自动转入收款地址
      console.log(`Minting fee ${fee.actualFee} ${tokenSymbol} sent to ${this.feeRecipientAddress}`);

      return {
        success: true,
        txHash
      };
    } catch (error) {
      console.error('Minting failed:', error);
      return {
        success: false
      };
    }
  }

  public async burnPrivateToken(
    userAddress: string,
    tokenSymbol: string,
    amount: string
  ): Promise<{ success: boolean; txHash?: string }> {
    try {
      const fee = this.calculateTransferFee(amount, tokenSymbol);
      
      // 模拟销毁隐私代币
      await new Promise(resolve => setTimeout(resolve, 2000));

      const txHash = '0x' + Math.random().toString(16).substr(2, 64);

      // 费用自动转入收款地址
      console.log(`Burning fee ${fee.actualFee} ${tokenSymbol} sent to ${this.feeRecipientAddress}`);

      return {
        success: true,
        txHash
      };
    } catch (error) {
      console.error('Burning failed:', error);
      return {
        success: false
      };
    }
  }

  public getFeeInfo(): { percentage: number; minFee: string; recipient: string } {
    return {
      percentage: this.feePercentage,
      minFee: this.minFeeAmount,
      recipient: this.feeRecipientAddress
    };
  }

  // 获取代币价格信息（用于显示）
  public async getTokenPrices(): Promise<Record<string, number>> {
    return {
      'ETH': 2000,
      'USDT': 1,
      'USDC': 1,
      'BNB': 300,
      'WBTC': 45000,
      'TRN': 0.1
    };
  }
}

// 导出单例实例
export const tokenManager = TokenManager.getInstance();
export type { TokenInfo, TokenBalance, TransferFee };
